import { createSelector } from 'reselect'
import { DateTime } from 'luxon'
import { Decimal } from 'decimal.js'
import _orderBy from 'lodash/orderBy'
import _filter from 'lodash/filter'
import _identity from 'lodash/identity'

import {
  MIN_AGE_BY_REGION,
  PHONE_FORMAT_BY_REGION,
  COUNTRY_DISPLAY,
  FETCH_STATUSES,
  REWARDS_LEVEL,
  REWARDS_FLAT_AMOUNTS,
  SUBSCRIPTION_TYPE,
} from 'resources'
import {
  getEnableReferral,
  getReferrerBonusLimit,
  getReferralRewardsRatio,
  getWeeklyPaycheckDay,
  getSubscriptionBasicVerifiedIdLimit,
  getSubscriptionLiteVerifiedIdLimit,
  getSubscriptionProVerifiedIdLimit,
} from 'models/General/selectors'

import { getPaycheckIntervalFromDateTime } from 'utils/date-time'

import {
  PHONE_VERIFY_STATUS,
  REWARDS_LEVEL_REQUIREMENT_KEYS,
  KILLI_PAYCHECK_MAX_TIER,
  EARN_MORE_FLOW_SCREEN_KEYS,
} from './constants'

export const getProfileInfo = (state) => state.profileInfo.data

export const getLegacyEmailAddress = createSelector(getProfileInfo, (profileData) =>
  profileData.email && profileData.email.value !== null ? profileData.email.value : undefined,
)

export const getLegacyPhoneNumber = createSelector(getProfileInfo, (profileData) =>
  profileData.phoneNumber ? profileData.phoneNumber.value : null,
)

export const getEmailAddresses = ({ profileInfo }) => profileInfo.emailAddresses ?? []
export const getPhoneNumbers = ({ profileInfo }) => profileInfo.phoneNumbers ?? []

export const getMultiIDPrimaryEmailAddress = createSelector(getEmailAddresses, (emailAddresses) => {
  const primaryEmailAddress = emailAddresses.find((value) => value.isPrimary)

  return primaryEmailAddress?.emailAddress ?? null
})

export const getMultiIDPrimaryPhoneNumber = createSelector(getPhoneNumbers, (phoneNumbers) => {
  const primaryPhoneNumber = phoneNumbers.find((value) => value.isPrimary)

  return primaryPhoneNumber?.phoneNumber ?? null
})

// TODO: Temporary solution for backward compatibility and it is to be replaced/refactored with getMultiIDPrimary* logic
export const getPrimaryEmailAddress = createSelector(
  getMultiIDPrimaryEmailAddress,
  getLegacyEmailAddress,
  (primaryEmailAddress, legacyEmail) => primaryEmailAddress || legacyEmail,
)

export const getPrimaryPhoneNumber = createSelector(
  getMultiIDPrimaryPhoneNumber,
  getLegacyPhoneNumber,
  (primaryPhoneNumber, legacyPhoneNumber) => primaryPhoneNumber || legacyPhoneNumber,
)

export const getSortedEmailAddresses = createSelector(getEmailAddresses, (emailAddresses) =>
  _orderBy(emailAddresses, ['isPrimary', 'isVerified', 'emailAddress'], ['desc', 'desc', 'asc']),
)

export const getSortedPhoneNumbers = createSelector(getPhoneNumbers, (phoneNumbers) =>
  _orderBy(phoneNumbers, ['isPrimary', 'isVerified', 'phoneNumber'], ['desc', 'desc', 'asc']),
)

export const getVerifiedEmailAddresses = createSelector(getEmailAddresses, (emailAddresses) =>
  _filter(emailAddresses, ['isVerified', true]),
)

export const getVerifiedPhoneNumbers = createSelector(getPhoneNumbers, (phoneNumbers) =>
  _filter(phoneNumbers, ['isVerified', true]),
)

export const getHasOnlyPrimaryEmailAddress = createSelector(
  getVerifiedEmailAddresses,
  (verifiedEmailAddresses) => verifiedEmailAddresses.length === 1,
)

export const getHasOnlyPrimaryPhoneNumber = createSelector(
  getVerifiedPhoneNumbers,
  (verifiedPhoneNumbers) => verifiedPhoneNumbers.length === 1,
)

export const getHasPendingUnveilNotification = ({ profileInfo }) => profileInfo.hasPendingUnveilNotification

export const getCanDeletePrimaryId = createSelector(
  getHasOnlyPrimaryEmailAddress,
  getHasOnlyPrimaryPhoneNumber,
  (hasOnlyPrimaryEmailAddress, hasOnlyPrimaryPhoneNumber) => hasOnlyPrimaryEmailAddress && hasOnlyPrimaryPhoneNumber,
)

export const getVerifiedIdsCount = createSelector(
  getVerifiedEmailAddresses,
  getVerifiedPhoneNumbers,
  (verifiedEmailAddresses, verifiedPhoneNumbers) => verifiedEmailAddresses.length + verifiedPhoneNumbers.length,
)

// Subscription
export const getSubscriptionType = ({ profileInfo }) => profileInfo.subscriptionType
export const getSubscriptionBillingCycle = ({ profileInfo }) => profileInfo.subscriptionBillingCycle

export const getSubscriptionExpiryDatetime = ({ profileInfo }) => profileInfo.subscriptionExpiryDatetime
export const getIsSubscriptionRenewalEnabled = ({ profileInfo }) => profileInfo.isSubscriptionRenewalEnabled

export const getIdsLimitBySubscriptionTypeMap = createSelector(
  getSubscriptionBasicVerifiedIdLimit,
  getSubscriptionLiteVerifiedIdLimit,
  getSubscriptionProVerifiedIdLimit,
  (subscriptionBasicVerifiedIdLimit, subscriptionLiteVerifiedIdLimit, subscriptionProVerifiedIdLimit) => ({
    [SUBSCRIPTION_TYPE.BASIC]: subscriptionBasicVerifiedIdLimit,
    [SUBSCRIPTION_TYPE.LITE]: subscriptionLiteVerifiedIdLimit,
    [SUBSCRIPTION_TYPE.PRO]: subscriptionProVerifiedIdLimit,
  }),
)

export const getCurrentIdsLimit = createSelector(
  getIdsLimitBySubscriptionTypeMap,
  getSubscriptionType,
  (idsLimitBySubscriptionTypeMap, subscriptionType) =>
    idsLimitBySubscriptionTypeMap[subscriptionType] ?? idsLimitBySubscriptionTypeMap[SUBSCRIPTION_TYPE.BASIC],
)

export const getHasReachedIdsLimit = createSelector(
  getVerifiedIdsCount,
  getCurrentIdsLimit,
  (verifiedIdsCount, currentIdsLimit) => verifiedIdsCount >= currentIdsLimit,
)

export const getHasPrimaryPhoneNumber = createSelector(getPrimaryPhoneNumber, (phoneNumber) => Boolean(phoneNumber))
export const getHasPrimaryEmailAddress = createSelector(getPrimaryEmailAddress, (email) => !!email)

export const getAdvertisingId = createSelector(getProfileInfo, (profileData) =>
  profileData.advertisingId ? profileData.advertisingId.value : null,
)

export const getPostalCode = createSelector(getProfileInfo, (profileData) =>
  profileData.postalCode && profileData.postalCode.value ? profileData.postalCode.value : null,
)

export const getDateOfBirth = createSelector(getProfileInfo, (profileData) =>
  profileData.dateOfBirth ? profileData.dateOfBirth.value : null,
)

export const getRegion = createSelector(getProfileInfo, (profileData) =>
  profileData.region && profileData.region.value ? profileData.region.value : 'CA',
)

export const getMinAge = createSelector(getRegion, (region) => MIN_AGE_BY_REGION[region])

export const getPhoneFormat = (state) => PHONE_FORMAT_BY_REGION[getRegion(state)]

export const getRegionDisplay = (state) => COUNTRY_DISPLAY[getRegion(state)]

export const getMaxDateOfBirth = createSelector(getMinAge, (minAge) =>
  DateTime.local().minus({ years: minAge }).toFormat('yyyy-MM-dd'),
)

export const getBalance = (state) => state.profileInfo.balance

export const getUserId = (state) => state.profileInfo.id

export const getPhoneVerifyStatus = (state) => state.profileInfo.phoneVerifyStatus

export const phoneStatusVerifying = createSelector(
  getPhoneVerifyStatus,
  (status) => status === PHONE_VERIFY_STATUS.phoneStatusVerifying,
)

export const phoneStatusSuccess = createSelector(
  getPhoneVerifyStatus,
  (status) => status === PHONE_VERIFY_STATUS.phoneStatusSuccess,
)

export const phoneStatusFailure = createSelector(
  getPhoneVerifyStatus,
  (status) => status === PHONE_VERIFY_STATUS.phoneStatusFailure,
)

export const isPhoneVerifying = createSelector(
  getHasPrimaryPhoneNumber,
  phoneStatusVerifying,
  (verified, isVerifying) => !verified && isVerifying,
)

export const getSendingSMSStatus = (state) => state.profileInfo.sendingSMSCodeStatus

export const getStatus = (state) => state.profileInfo.status

export const statusUpdating = createSelector(getStatus, (status) => status === 'updating')

export const statusSuccess = createSelector(getStatus, (status) => status === 'success')

export const getIsFetchedAtLeastOnce = (state) => state.profileInfo.isFetchedAtLeastOnce

export const statusFailure = createSelector(getStatus, (status) => status === 'failure')

export const getUserCreationDate = (state) => state.profileInfo.creationDate

export const getSellProfileData = (state) => state.profileInfo.sellProfileData

export const getAccountAge = (state) => Math.floor((new Date() - new Date(getUserCreationDate(state))) / 86400000)

export const isEmailUpdating = (state) => state.profileInfo.updateEmailStatus === 'loading'

export const isEmailUpdateSuccess = (state) => state.profileInfo.updateEmailStatus === 'success'

export const isCodeVerifying = (state) =>
  state.profileInfo.phoneVerifyStatus === PHONE_VERIFY_STATUS.phoneStatusVerifying
export const hasCodeFailedVerification = (state) =>
  state.profileInfo.phoneVerifyStatus === PHONE_VERIFY_STATUS.phoneStatusFailure

export const getGender = createSelector(getProfileInfo, (profileData) =>
  profileData.gender ? profileData.gender.value : null,
)

export const getUserAlias = (state) => state.profileInfo.userAlias

// Social Share
export const getBranchRedemptionLink = (state) => state.profileInfo.branchRedemptionLink

// ----------------
// Refer A Friend
// ----------------

export const getReferralRefereeCount = ({ profileInfo }) => profileInfo.referralRefereeCount

export const getReferralLink = (state) => state.profileInfo.referralLink

export const getReferralBonusTotalAmount = (state) => state.profileInfo.referralBonusTotalAmount

export const getReferralRewardsTotalAmount = (state) => state.profileInfo.referralRewardsTotalAmount

export const getReferralTotalEarnedAmount = createSelector(
  [getReferralBonusTotalAmount, getReferralRewardsTotalAmount],
  (referralBonusTotalAmount, referralRewardsTotalAmount) =>
    parseFloat(referralBonusTotalAmount) + parseFloat(referralRewardsTotalAmount),
)

const getRefereePaycheckTierCounts = ({ profileInfo }) => profileInfo.referralRefereePaycheckTierCounts

export const getNextReferralPaycheckPayout = createSelector(
  [getReferralRewardsRatio, getRefereePaycheckTierCounts],
  (referralRewardsRatio, refereePaycheckTierCounts) =>
    Object.values(REWARDS_LEVEL)
      .reduce((totalValue, tier) => {
        try {
          const paycheckAmount = new Decimal(REWARDS_FLAT_AMOUNTS[tier])
          const rewardBonusForTier = paycheckAmount.mul(referralRewardsRatio).toDecimalPlaces(2, Decimal.ROUND_UP)
          return rewardBonusForTier.mul(refereePaycheckTierCounts[tier]).plus(totalValue)
        } catch (error) {
          return totalValue
        }
      }, new Decimal(0))
      .toNumber(),
)

export const getIsReferredUser = (state) => state.profileInfo.isReferredUser

export const getIsLockedByAdmin = (state) => state.profileInfo.isLockedByAdmin
export const getJackpotAutoPledgeAmount = (state) => state.profileInfo.jackpotAutoPledgeAmount
export const getAutoRedeemSharesEnabled = (state) => state.profileInfo.autoRedeemSharesEnabled

export const getIsReferBonusAvailable = createSelector(
  [getEnableReferral, getReferrerBonusLimit, getReferralRefereeCount],
  (enableReferral, referrerBonusLimit, referralRefereeCount) =>
    enableReferral && referralRefereeCount < referrerBonusLimit,
)

export const getIsUnveilOnboarded = (state) => state.profileInfo.isUnveilOnboarded
export const getCreateAccountType = (state) => state.profileInfo.createAccountType
export const getIsKidProfilesChecked = (state) => state.profileInfo.isKidProfilesChecked
export const getKidProfilesLastCheckedDatetime = (state) => state.profileInfo.kidProfilesLastCheckedDatetime
export const getHasMatchingKidProfiles = (state) => state.profileInfo.hasMatchingKidProfiles
export const getIsUnveilPrompted = (state) => state.profileInfo.isUnveilPrompted
export const getIsUnveilSeen = createSelector(
  [getIsUnveilOnboarded, getIsUnveilPrompted],
  (isUnveilOnboarded, isUnveilPrompted) => isUnveilOnboarded && isUnveilPrompted,
)

export const hasMinAccountBalance = ({ profileInfo }) => profileInfo.hasMinAccountBalance

export const getTotalRewardsEarnings = ({ profileInfo }) => profileInfo.totalEarnings

export const getIsEmailNotificationsEnabled = ({ profileInfo }) => profileInfo.enableEmailNotifications

export const getIsSMSNotificationsEnabled = ({ profileInfo }) => profileInfo.enableSmsNotifications

export const isPushNotificationsEnabled = ({ profileInfo }) => profileInfo.enablePushNotifications

export const getDateOfBirthSource = createSelector(getProfileInfo, (profileData) => profileData.dateOfBirth.source)
export const getGenderSource = createSelector(getProfileInfo, (profileData) => profileData.gender.source)
export const getPostalCodeSource = createSelector(getProfileInfo, (profileData) => profileData.postalCode.source)

export const getCountrySource = createSelector(getProfileInfo, (profileData) => profileData.region.source)

export const getIsFetchingProfileInfo = ({ profileInfo }) => profileInfo.status === FETCH_STATUSES.FETCHING

const getHasActiveShoppingRewardsAccount = ({ profileInfo }) => profileInfo.hasActiveShoppingRewardsAccount
const getShoppingRewardsLatestEventDatetime = ({ profileInfo }) => profileInfo.shoppingRewardsLatestEventDatetime
const getBrowserRewardsLatestEventDatetime = ({ profileInfo }) => profileInfo.browserRewardsLatestEventDatetime
const checkDatetimeWithinInterval = (targetDatetimeIso, interval) => {
  if (!targetDatetimeIso) {
    return false
  }

  const targetDateTime = DateTime.fromISO(targetDatetimeIso)
  return interval.contains(targetDateTime)
}

const getCurrentPaycheckInterval = createSelector(getWeeklyPaycheckDay, (weeklyPaycheckDay) => {
  const now = DateTime.local()
  return getPaycheckIntervalFromDateTime(now, weeklyPaycheckDay)
})

// Profile Rewards
export const getProfileRewardsShareDatetime = ({ profileInfo }) => profileInfo.profileRewardsShareDatetime
export const getIsProfileRewardsMember = ({ profileInfo }) => profileInfo.isProfileRewardsMember
export const getCompletedRequiredProfileRewardsQuestions = ({ profileInfo }) =>
  profileInfo.completedRequiredProfileRewardsQuestions

export const getIsProfileRewardsCompleted = getCompletedRequiredProfileRewardsQuestions

export const getIsProfileRewardsSubTasksCompleted = createSelector(
  [getIsProfileRewardsMember, getIsProfileRewardsCompleted],
  (isProfileRewardsMember, isProfileRewardsCompleted) => isProfileRewardsMember && isProfileRewardsCompleted,
)

export const getIsProfileRewardsInProgress = getCompletedRequiredProfileRewardsQuestions
export const getIsProfileRewardsEligible = getIsProfileRewardsSubTasksCompleted

// Shopping Rewards
export const getIsShoppingRewardsMember = ({ profileInfo }) => profileInfo.isFinancialDividendMember
export const getIsShoppingRewardsMemberAndAccountConnected = createSelector(
  [getIsShoppingRewardsMember, getHasActiveShoppingRewardsAccount],
  (isFinancialDividendMember, hasActiveShoppingRewardsAccount) =>
    isFinancialDividendMember && hasActiveShoppingRewardsAccount,
)
export const getIsShoppingRewardsMemberAndAccountRemoved = createSelector(
  [getIsShoppingRewardsMember, getHasActiveShoppingRewardsAccount],
  (isFinancialDividendMember, hasActiveShoppingRewardsAccount) =>
    isFinancialDividendMember && !hasActiveShoppingRewardsAccount,
)

const getIsShoppingRewardsHasPaycheckWeekEventSelector = createSelector(
  [getShoppingRewardsLatestEventDatetime, getCurrentPaycheckInterval],
  checkDatetimeWithinInterval,
)
export const getIsShoppingRewardsHasPaycheckWeekEvent = createSelector(
  [getIsShoppingRewardsMember, getIsShoppingRewardsHasPaycheckWeekEventSelector],
  (isFinancialDividendMember, hasPaycheckWeekEvent) => isFinancialDividendMember && hasPaycheckWeekEvent,
)

export const getIsShoppingRewardsSubTasksCompleted = createSelector(
  [getIsShoppingRewardsMemberAndAccountConnected, getIsShoppingRewardsHasPaycheckWeekEvent],
  (isShoppingRewardsMemberAndAccountConnected, isShoppingRewardsHasPaycheckWeekEvent) =>
    isShoppingRewardsMemberAndAccountConnected && isShoppingRewardsHasPaycheckWeekEvent,
)

export const getIsShoppingRewardsInProgress = getIsShoppingRewardsMember
export const getIsShoppingRewardsEligible = getIsShoppingRewardsSubTasksCompleted

// Browsing Rewards
export const getIsBrowserRewardsMember = ({ profileInfo }) => profileInfo.isBrowserDividendMember
export const getIsBrowsingRewardsHasPaycheckWeekEvent = createSelector(
  [getBrowserRewardsLatestEventDatetime, getCurrentPaycheckInterval],
  checkDatetimeWithinInterval,
)

export const getIsBrowsingRewardsSubTasksCompleted = createSelector(
  [getIsBrowserRewardsMember, getIsBrowsingRewardsHasPaycheckWeekEvent],
  (isBrowsingRewardsMember, isBrowsingRewardsHasPaycheckWeekEvent) =>
    isBrowsingRewardsMember && isBrowsingRewardsHasPaycheckWeekEvent,
)

export const getUserCode = ({ profileInfo }) => profileInfo.userCode

export const getIsVerifiedUser = (state) => !!state.profileInfo.isVerifiedUser
export const getIsVip = (state) => !!state.profileInfo.isVipReferrer

export const getIsBrowsingRewardsInProgress = getIsBrowserRewardsMember
export const getIsBrowsingRewardsEligible = getIsBrowsingRewardsSubTasksCompleted

// Device Rewards
export const getIpAddress = ({ profileInfo }) => profileInfo.ipAddress
export const getDeviceRewardsDeviceData = ({ profileInfo }) => profileInfo.deviceRewardsDeviceDataJson
export const getDeviceRewardsAppsData = ({ profileInfo }) => profileInfo.deviceRewardsAppsDataJson

export const getAppInstallStatuses = createSelector(
  getDeviceRewardsAppsData,
  (deviceRewardsAppsData) => deviceRewardsAppsData && deviceRewardsAppsData.appInstallStatuses,
)

export const getIsDeviceRewardsMember = ({ profileInfo }) => profileInfo.isDeviceRewardsMember
export const getIsDeviceRewardsMemberSubTasksCompleted = getIsDeviceRewardsMember
export const getIsDeviceRewardsInProgress = getIsDeviceRewardsMember
export const getIsDeviceRewardsEligible = getIsDeviceRewardsMemberSubTasksCompleted

// Carousel Cards
const getReklaimCarouselSubtaskCompletions = createSelector(
  [getIsProfileRewardsSubTasksCompleted, getIsDeviceRewardsMemberSubTasksCompleted],
  (isProfileRewardsSubTasksCompleted, isDeviceRewardsSubTasksCompleted) => ({
    [REWARDS_LEVEL_REQUIREMENT_KEYS.AUDIENCE_DIVIDEND]: isProfileRewardsSubTasksCompleted,
    [REWARDS_LEVEL_REQUIREMENT_KEYS.DEVICE_DIVIDEND]: isDeviceRewardsSubTasksCompleted,
  }),
)

const getCurrentReklaimCarouselCards = createSelector(
  getReklaimCarouselSubtaskCompletions,
  (reklaimCarouselSubtaskCompletions) =>
    Object.entries(reklaimCarouselSubtaskCompletions).reduce((acc, [cardKey, isSubtasksCompleted]) => {
      if (!isSubtasksCompleted) {
        return [...acc, cardKey]
      }
      return acc
    }, []),
)

export const getRewardsLevel = createSelector([getReklaimCarouselSubtaskCompletions], (requirements) =>
  Math.min(
    Object.values(requirements).reduce((acc, curr) => (curr ? acc + 1 : acc), 0),
    KILLI_PAYCHECK_MAX_TIER,
  ),
)

export const getPendingNotificationModalType = (state) => state.profileInfo.pendingNotificationModalType

export const getSendSmsErrorMessage = (state) => state.profileInfo.sendSmsErrorMessage

export const getBrowsingRewardsStatistics = ({ profileInfo }) => profileInfo.browsingRewardsStatistics

// Earn More Flow Selectors
const getEarnMoreFlowScreenStatuses = createSelector(
  [getIsProfileRewardsSubTasksCompleted],
  (isProfileRewardsSubTasksCompleted) => ({
    [EARN_MORE_FLOW_SCREEN_KEYS.PROFILE_REWARDS]: isProfileRewardsSubTasksCompleted,
  }),
)
export const getEarnMoreFlowCompletedScreens = createSelector(
  getEarnMoreFlowScreenStatuses,
  (earnMoreFlowScreenStatuses) =>
    Object.entries(earnMoreFlowScreenStatuses).reduce(
      (acc, [screenKey, isComplete]) => (isComplete ? [...acc, screenKey] : acc),
      [],
    ),
)
export const getIsEarnMoreFlowCompleted = createSelector(getEarnMoreFlowScreenStatuses, (earnMoreFlowScreenStatuses) =>
  Object.values(earnMoreFlowScreenStatuses).every(_identity),
)

// Personalization
export const getUserHandle = ({ profileInfo }) => profileInfo.userHandle
export const getProfilePictureUrl = ({ profileInfo }) => profileInfo.profilePictureUrl

export const getHasPendingTransactions = ({ profileInfo }) => profileInfo.hasPendingTransactions
export const getPendingTransactionsAmount = ({ profileInfo }) => profileInfo.pendingTransactionsAmount

export default {
  isCodeVerifying,
  hasCodeFailedVerification,
  getProfileInfo,
  getLegacyEmailAddress,
  getPrimaryEmailAddress,
  getHasPrimaryEmailAddress,
  getEmailAddresses,
  getSortedEmailAddresses,
  getVerifiedEmailAddresses,
  getLegacyPhoneNumber,
  getPrimaryPhoneNumber,
  getHasPrimaryPhoneNumber,
  getHasPendingUnveilNotification,
  getPhoneNumbers,
  getSortedPhoneNumbers,
  getVerifiedPhoneNumbers,
  getCanDeletePrimaryId,
  getAdvertisingId,
  getDateOfBirth,
  getMaxDateOfBirth,
  getMinAge,
  getPhoneFormat,
  getRegion,
  getRegionDisplay,
  getUserCreationDate,
  getAccountAge,
  getUserId,
  phoneStatusVerifying,
  phoneStatusSuccess,
  phoneStatusFailure,
  getPhoneVerifyStatus,
  getSendingSMSStatus,
  statusUpdating,
  statusSuccess,
  statusFailure,
  getBalance,
  getGender,
  getStatus,
  isEmailUpdating,
  isEmailUpdateSuccess,
  isPhoneVerifying,
  getSellProfileData,
  getReferralLink,
  getReferralRefereeCount,
  getReferralBonusTotalAmount,
  getReferralTotalEarnedAmount,
  getIsReferBonusAvailable,
  getNextReferralPaycheckPayout,
  getIsReferredUser,
  getUserAlias,
  getIsProfileRewardsEligible,
  getIsShoppingRewardsMember,
  getIsFetchedAtLeastOnce,
  getRewardsLevel,
  getReklaimCarouselSubtaskCompletions,
  getCurrentReklaimCarouselCards,
  getIsBrowserRewardsMember,
  hasMinAccountBalance,
  getCompletedRequiredProfileRewardsQuestions,
  getProfileRewardsShareDatetime,
  getTotalRewardsEarnings,
  getIsEmailNotificationsEnabled,
  getIsSMSNotificationsEnabled,
  getPostalCode,
  getDateOfBirthSource,
  getGenderSource,
  getPostalCodeSource,
  getCountrySource,
  isPushNotificationsEnabled,
  getIsFetchingProfileInfo,
  getIsProfileRewardsMember,
  getIsProfileRewardsCompleted,
  getIsProfileRewardsInProgress,
  getIsProfileRewardsSubTasksCompleted,
  getIsShoppingRewardsMemberAndAccountConnected,
  getIsShoppingRewardsHasPaycheckWeekEvent,
  getIsShoppingRewardsSubTasksCompleted,
  getIsShoppingRewardsInProgress,
  getIsShoppingRewardsMemberAndAccountRemoved,
  getIsShoppingRewardsEligible,
  getIsBrowsingRewardsHasPaycheckWeekEvent,
  getIsBrowsingRewardsSubTasksCompleted,
  getUserCode,
  getIsVerifiedUser,
  getIsVip,
  getIsBrowsingRewardsInProgress,
  getIsBrowsingRewardsEligible,
  getIpAddress,
  getDeviceRewardsDeviceData,
  getDeviceRewardsAppsData,
  getAppInstallStatuses,
  getIsDeviceRewardsInProgress,
  getIsDeviceRewardsEligible,
  getIsDeviceRewardsMember,
  getIsDeviceRewardsMemberSubTasksCompleted,
  getPendingNotificationModalType,
  getIsLockedByAdmin,
  getJackpotAutoPledgeAmount,
  getIsUnveilOnboarded,
  getCreateAccountType,
  getIsKidProfilesChecked,
  getKidProfilesLastCheckedDatetime,
  getHasMatchingKidProfiles,
  getIsUnveilPrompted,
  getIsUnveilSeen,
  getBrowsingRewardsStatistics,
  getVerifiedIdsCount,
  getSubscriptionType,
  getSubscriptionBillingCycle,
  getIsSubscriptionRenewalEnabled,
  getIdsLimitBySubscriptionTypeMap,
  getCurrentIdsLimit,
  getHasReachedIdsLimit,
  getEarnMoreFlowCompletedScreens,
  getIsEarnMoreFlowCompleted,
  getUserHandle,
  getProfilePictureUrl,
  getHasPendingTransactions,
  getPendingTransactionsAmount,
}
